import React, { useEffect, useState } from "react";
import {
  Drawer,
  Typography,
  IconButton,
  Divider,
  Grid,
  Tab,
  Box,
  Menu,
  MenuItem,
} from "@mui/material";
import { PatientDto } from "../../../api-client";
import PatientOverview from "../../../components/PatientOverview/PatientOverview";
import CloseIcon from "@mui/icons-material/Close";
import { TabContext, TabList } from "@mui/lab";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import { observer } from "mobx-react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { PushPin } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import useStore from "../../../helpers/useStore";
import PatientRxTabs from "./PatientRxTabs";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { DrawerHeader } from "../../../components/MenuBars/SideDrawer";
import { runInAction } from "mobx";
import ScheduleContext from "./ScheduleContext";
import ContinuousAppointmentCard from "./ContinuousAppointmentCard";
import EventShelf from "../EventShelf/EventShelf";

interface CalendarDrawerProps {
  open?: boolean;
  patient?: PatientDto;
  drawerWidth?: number;
  showOpenButton?: boolean;
  onPatientTabChange?: (newValue: string | number) => void;
  onTargetPatientChange?: (patient: PatientDto) => void;
}

const CalendarDrawer: React.FC<CalendarDrawerProps> = observer(
  ({ open, patient: patientProp, drawerWidth = 320, showOpenButton, onPatientTabChange }) => {
    // Access CalendarStore to get state that was previously passed as props
    const CalendarStore = useStore(CalendarContext);
    const {
      targetPatientId,
      targetPatientLastName,
      selectedPatientId,
      selectedPatientLastName,
      secondaryPatientId,
      secondaryPatientLastName,
      continuousView,
    } = CalendarStore;

    // logic to determine left/right patient tabs
    // If there is a selectedPatientId but no target, then the left tab is used and the right tab is empty
    // if a target exists, it is always on the left
    // if a secondary exists, it is always on the right
    // however, if a target exists, and the selected is different, then the selected is on the right
    let leftPatientId: number | null = null;
    let leftPatientLastName: string | null = null;
    let rightPatientId: number | null = null;
    let rightPatientLastName: string | null = null;

    if (targetPatientId) {
      leftPatientId = targetPatientId;
      leftPatientLastName = targetPatientLastName || null;
      if (selectedPatientId && selectedPatientId !== targetPatientId) {
        rightPatientId = selectedPatientId;
        rightPatientLastName = selectedPatientLastName || null;
      } else if (secondaryPatientId && secondaryPatientId !== targetPatientId) {
        rightPatientId = secondaryPatientId;
        rightPatientLastName = secondaryPatientLastName || null;
      }
    } else if (selectedPatientId) {
      leftPatientId = selectedPatientId;
      leftPatientLastName = selectedPatientLastName || null;
    }

    const nameMap = new Map<number, string>();
    if (targetPatientId && targetPatientLastName) {
      nameMap.set(targetPatientId, targetPatientLastName);
    }
    if (selectedPatientId && selectedPatientLastName) {
      nameMap.set(selectedPatientId, selectedPatientLastName);
    }
    if (secondaryPatientId && secondaryPatientLastName) {
      nameMap.set(secondaryPatientId, secondaryPatientLastName);
    }

    // Event handlers that update the store instead of calling props
    const onClose = () => CalendarStore.setPatientDrawerOpen(false);
    const onOpen = () => CalendarStore.setPatientDrawerOpen(true);

    // const [tabValuePatient, setTabValuePatient] = useState(""); // patient tab value (target vs secondary)
    const [contextMenu, setContextMenu] = useState<{
      mouseX: number;
      mouseY: number;
      patientId: number;
    } | null>(null);

    const handleSelectedPatientChange = (event: React.SyntheticEvent, newValue: number) => {
      const patientId = newValue;

      runInAction(() => {
        // if the current selected patient is not the target, and the new value is the target,
        // then we do need to store the id and name in secondaryPatientId and lastName
        if (selectedPatientId) {
          if (patientId === targetPatientId) {
            CalendarStore.setSecondaryPatientId(selectedPatientId);
            CalendarStore.setSecondaryPatientLastName(nameMap.get(selectedPatientId) || "");
          } else {
            CalendarStore.setSecondaryPatientId(undefined);
            CalendarStore.setSecondaryPatientLastName("");
          }
        }
        // Only set selected patient, DO NOT overwrite target unless it was empty (handled inside store)
        CalendarStore.setSelectedPatientId(patientId);
        CalendarStore.setSelectedPatientLastName(nameMap.get(patientId) || "");
      });
      onPatientTabChange?.(newValue);
    };

    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, patientId: number) => {
      event.preventDefault();
      setContextMenu({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
        patientId: patientId,
      });
    };

    // closing the context menu for pinning
    const handleClose = () => {
      setContextMenu(null);
    };

    const handleUnpin = () => {
      runInAction(() => {
        CalendarStore.setTargetPatientId(undefined);
        CalendarStore.setTargetPatientLastName(undefined);
      });
      handleClose();
    };

    const handlePinToggle = (pId: number) => {
      runInAction(() => {
        CalendarStore.setTargetPatientId(pId);
        CalendarStore.setTargetPatientLastName(nameMap.get(pId) || "");
      });
      handleClose();
    };

    return (
      <>
        {showOpenButton && !open && (
          <IconButton onClick={onOpen} sx={{ position: "fixed", left: 5, zIndex: 1200 }}>
            <ArrowForwardIosIcon />
          </IconButton>
        )}
        {open && (
          <Drawer
            open={open}
            onClose={onClose}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
                backgroundColor: `${mobiliTheme.topLevelBackground}`,
                marginLeft: 5, // 40px is the width of the side menu
                marginTop: 8, // 64px is the height of the appbar
              },
            }}
            variant="persistent"
          >
            <Grid padding={1}>
              {selectedPatientId && (
                <Typography>
                  <DrawerHeader sx={{ minHeight: "0 !important", justifyContent: "space-between" }}>
                    <TabContext value={selectedPatientId}>
                      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <TabList
                          onChange={handleSelectedPatientChange}
                          variant="scrollable"
                          scrollButtons="auto"
                        >
                          {leftPatientId && leftPatientLastName && (
                            <Tab
                              value={leftPatientId}
                              key={`target-${leftPatientId}`}
                              label={
                                <span>
                                  {leftPatientLastName}
                                  {targetPatientId && (
                                    <PushPin
                                      sx={{
                                        fontSize: "0.75rem",
                                        position: "absolute",
                                        top: 3,
                                        right: 3,
                                      }}
                                    />
                                  )}
                                </span>
                              }
                              onContextMenu={(e) => handleRightClick(e, leftPatientId)}
                            />
                          )}
                          {rightPatientId && rightPatientLastName && (
                            <Tab
                              value={rightPatientId}
                              key={`secondary-${rightPatientId}`}
                              label={rightPatientLastName}
                              onContextMenu={(e) => handleRightClick(e, rightPatientId)}
                            />
                          )}
                        </TabList>
                        {/* Context Menu */}
                        <Menu
                          open={contextMenu !== null}
                          onClose={handleClose}
                          anchorReference="anchorPosition"
                          anchorPosition={
                            contextMenu !== null
                              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                              : undefined
                          }
                        >
                          {contextMenu?.patientId !== targetPatientId && (
                            <MenuItem onClick={() => handlePinToggle(contextMenu!.patientId)}>
                              Pin Patient
                            </MenuItem>
                          )}
                          <MenuItem onClick={handleUnpin}>Unpin</MenuItem>
                        </Menu>
                      </Box>
                    </TabContext>
                    <IconButton onClick={onClose}>
                      <CloseIcon />
                    </IconButton>
                  </DrawerHeader>
                  {selectedPatientId && (
                    <PatientOverview patientId={selectedPatientId} linkOut="patientPage" />
                  )}
                  {/* Debug-window prototype (CalendarStore.shelfInDrawer):
                      the planning shelf as a drawer section instead of a band
                      by the grid. Same component and drag payloads — sections
                      stack and cards wrap to the drawer width. */}
                  {CalendarStore.shelfInDrawer && selectedPatientId && (
                    <Box sx={{ my: 0.5 }}>
                      <EventShelf layout="column" placement="above" />
                    </Box>
                  )}
                  {selectedPatientId && (
                    continuousView 
                      ? <ContinuousAppointmentCard patientId={selectedPatientId} />
                      : <ScheduleContext patientId={selectedPatientId} />
                  )}

                  {selectedPatientId && <PatientRxTabs patientId={selectedPatientId} />}
                  <Divider />
                </Typography>
              )}
            </Grid>
          </Drawer>
        )}
      </>
    );
  }
);

export default CalendarDrawer;
